/**
 * Dog management hook - handles all dog CRUD operations with nutrition tracking
 */

import { useCallback, useMemo, useState } from 'react';
import { caloriesFromGrams } from '@/domains/nutrition/FoodEnergy';
import { updateCustomerDog, softDeleteDog as softDeleteDogApi, restoreDog as restoreDogApi } from '../api/accountProxy';
import type {
  Dog,
  DogFormData,
  UpdateDogResult,
  NutritionRecalculationResult,
  NutritionHistoryEntry,
  ShopifyCustomer,
} from '../types';

interface UseDogsManagementProps {
  customer: ShopifyCustomer | null;
  dogs: Dog[];
  proxyBase: string;
  shop: string;
  recalculateDogNutrition: (dog: Dog) => NutritionRecalculationResult;
  onDogsUpdated: () => Promise<void>; // Refresh callback
  updateLocalDog: (dogId: string, updates: Partial<Dog>) => void; // Optimistic update
}

export function useDogsManagement({
  customer,
  dogs,
  proxyBase,
  shop,
  recalculateDogNutrition,
  onDogsUpdated,
  updateLocalDog,
}: UseDogsManagementProps) {
  const [updating, setUpdating] = useState(false);

  /**
   * Dogs indexed by ID for quick lookup
   */
  const dogsById = useMemo(() => {
    const map = new Map<string, Dog>();
    dogs.forEach((dog) => map.set(dog.id, dog));
    return map;
  }, [dogs]);

  /**
   * Check if updates affect nutrition calculation
   */
  const isNutritionAffecting = useCallback(
    (updates: Partial<DogFormData>): boolean => {
      const nutritionFields = [
        'weightKg',
        'ageYears',
        'ageMonths',
        'bodyCondition',
        'activityLevel',
        'treatFrequency',
      ];

      return Object.keys(updates).some((key) =>
        nutritionFields.includes(key)
      );
    },
    []
  );

  /**
   * Create nutrition history entry from current dog state
   */
  const createHistoryEntry = useCallback((dog: Dog): NutritionHistoryEntry => {
    return {
      weightKg: dog.weightKg,
      dailyGrams: dog.dailyGrams || dog.currentNutrition?.dailyGrams || 0,
      dailyCalories: dog.dailyCalories || dog.currentNutrition?.dailyCalories || 0,
      ageYears: dog.ageYears,
      ageMonths: dog.ageMonths,
      bodyCondition: dog.bodyCondition,
      activityLevel: dog.activityLevel,
      recordedAt: new Date().toISOString(),
    };
  }, []);

  /**
   * Update dog with nutrition recalculation if needed
   */
  const saveDogWithNutrition = useCallback(
    async (
      dogId: string,
      updates: Partial<DogFormData>
    ): Promise<NutritionRecalculationResult | null> => {
      setUpdating(true);

      try {
        const existingDog = dogsById.get(dogId);
        if (!existingDog) {
          throw new Error(`Dog with ID ${dogId} not found`);
        }

        // Merge updates with existing dog
        const updatedDog = {
          ...existingDog,
          ...updates,
          // Ensure age fields are synced
          ageYears: updates.ageYears ?? existingDog.ageYears,
          ageMonths: updates.ageMonths ?? existingDog.ageMonths,
        } as Dog;

        // Check if nutrition recalculation is needed
        const needsRecalculation = isNutritionAffecting(updates);

        let recalcResult: NutritionRecalculationResult | null = null;

        if (needsRecalculation) {
          // Recalculate nutrition
          recalcResult = recalculateDogNutrition(updatedDog);

          // Add current state to history before updating
          const historyEntry = createHistoryEntry(existingDog);
          const nutritionHistory = [
            ...(existingDog.nutritionHistory || []),
            historyEntry,
          ];

          // Update dog with new nutrition values
          updatedDog.currentNutrition = {
            dailyCalories: recalcResult.newNutrition.caloriesPerDay,
            dailyGrams: recalcResult.newNutrition.gramsPerDay,
            mealsPerRecipe: recalcResult.newNutrition.mealsPerRecipe,
            gramsPerRecipe: recalcResult.newNutrition.gramsPerRecipe,
            calculatedAt: new Date().toISOString(),
          };
          updatedDog.dailyGrams = recalcResult.newNutrition.gramsPerDay;
          updatedDog.dailyCalories = recalcResult.newNutrition.caloriesPerDay;
          updatedDog.nutritionHistory = nutritionHistory;

          // Update recalcResult with the complete dog
          recalcResult.dog = updatedDog;
        }

        // Keep stored calories in step with grams on every save, so profiles
        // holding the old pre-rounding MER value are corrected (metafield +
        // Klaviyo sync) even when the edit didn't touch nutrition.
        if (updatedDog.dailyGrams) {
          updatedDog.dailyCalories = caloriesFromGrams(updatedDog.dailyGrams);
        }
        if (updatedDog.currentNutrition?.dailyGrams) {
          updatedDog.currentNutrition = {
            ...updatedDog.currentNutrition,
            dailyCalories: caloriesFromGrams(updatedDog.currentNutrition.dailyGrams),
          };
        }

        // Save to backend (customer metafield update)
        const result = await updateCustomerDog(proxyBase, shop, {
          dogId,
          patch: updatedDog,
        });

        if (!result.ok) {
          throw new Error('Failed to save dog to backend');
        }

        // Optimistically update local state with the updated dog
        updateLocalDog(dogId, updatedDog);

        return recalcResult;
      } catch (error) {
        console.error('[saveDogWithNutrition] Failed to save dog:', error);
        // Re-throw to let caller handle the error
        throw error;
      } finally {
        setUpdating(false);
      }
    },
    [
      dogsById,
      isNutritionAffecting,
      recalculateDogNutrition,
      createHistoryEntry,
      proxyBase,
      shop,
      updateLocalDog,
    ]
  );

  /**
   * Update dog without nutrition recalculation (basic info only)
   */
  const updateDog = useCallback(
    async (
      dogId: string,
      updates: Partial<DogFormData>
    ): Promise<UpdateDogResult> => {
      setUpdating(true);

      try {
        const existingDog = dogsById.get(dogId);
        if (!existingDog) {
          return {
            ok: false,
            nutritionChanged: false,
            error: `Dog with ID ${dogId} not found`,
          };
        }

        // Save to backend
        const result = await updateCustomerDog(proxyBase, shop, {
          dogId,
          patch: updates,
        });

        if (!result.ok) {
          return {
            ok: false,
            nutritionChanged: false,
            error: 'Failed to save dog to backend',
          };
        }

        // Optimistically update local state
        updateLocalDog(dogId, updates as Partial<Dog>);

        return {
          ok: true,
          dog: { ...existingDog, ...updates } as Dog,
          nutritionChanged: false,
        };
      } catch (error) {
        return {
          ok: false,
          nutritionChanged: false,
          error: (error as Error).message,
        };
      } finally {
        setUpdating(false);
      }
    },
    [dogsById, proxyBase, shop, updateLocalDog]
  );

  /**
   * Get dog by ID
   */
  const getDogById = useCallback(
    (dogId: string): Dog | undefined => {
      return dogsById.get(dogId);
    },
    [dogsById]
  );

  /**
   * Soft-delete a dog (optimistic update + API call)
   */
  const softDeleteDog = useCallback(
    async (dogId: string): Promise<void> => {
      const existingDog = dogsById.get(dogId);
      if (!existingDog) return;

      // Optimistic update
      const deletedAt = new Date().toISOString();
      updateLocalDog(dogId, { deleted: true, deletedAt });

      try {
        const result = await softDeleteDogApi(proxyBase, shop, { dogId });
        if (!result.ok) {
          throw new Error(result.error || 'Failed to remove dog');
        }
      } catch (error) {
        // Revert on any error (network or API failure)
        updateLocalDog(dogId, { deleted: false, deletedAt: undefined });
        throw error;
      }
    },
    [dogsById, proxyBase, shop, updateLocalDog]
  );

  /**
   * Restore a soft-deleted dog (optimistic update + API call)
   */
  const restoreDog = useCallback(
    async (dogId: string): Promise<void> => {
      const existingDog = dogsById.get(dogId);
      if (!existingDog) return;

      const previousDeletedAt = existingDog.deletedAt;

      // Optimistic update
      updateLocalDog(dogId, { deleted: false, deletedAt: undefined });

      try {
        const result = await restoreDogApi(proxyBase, shop, { dogId });
        if (!result.ok) {
          throw new Error(result.error || 'Failed to restore dog');
        }
      } catch (error) {
        // Revert on any error (network or API failure)
        updateLocalDog(dogId, { deleted: true, deletedAt: previousDeletedAt });
        throw error;
      }
    },
    [dogsById, proxyBase, shop, updateLocalDog]
  );

  return {
    dogs,
    dogsById,
    updating,
    getDogById,
    updateDog,
    saveDogWithNutrition,
    softDeleteDog,
    restoreDog,
  };
}
